import { Head, usePage } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { DataTable, DataRow } from "@/components/ui/data-table"

import type { PageProps } from "@/types/inertia"

type ProfileProps = {
  profile: {
    name: string
    username: string | null
    email: string | null
    position: string | null
    org_claim: string | null
    organization: string | null
    department: string | null
    department_claim: string | null
    department_overridden: boolean
    last_login_at: string | null
  }
}

function formatDate(value: string | null) {
  if (!value) return "—"
  return new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  })
}

export default function ProfileDetails() {
  const { props } = usePage<PageProps<ProfileProps>>()
  const profile = props.profile

  return (
    <>
      <Head title="My details">
        <meta
          name="description"
          content="The account details Duo provides for you, refreshed at every sign-in."
        />
        <meta property="og:title" content="My details" />
        <meta
          property="og:description"
          content="The account details Duo provides for you, refreshed at every sign-in."
        />
      </Head>
      <AppShell>
        <PageHeader
          title="Profile"
          description="These details come from your IUPAT Duo account and refresh every time you sign in. To change them, ask an IUPAT administrator to update your Duo profile."
        />

        <section className="mt-10 max-w-xl">
          <DataTable>
            <DataRow title="Name">{profile.name}</DataRow>
            <DataRow title="Username">{profile.username ?? "—"}</DataRow>
            <DataRow title="Email">{profile.email ?? "—"}</DataRow>
            <DataRow title="Position">{profile.position ?? "—"}</DataRow>
            <DataRow title="Department">
              {profile.department ?? "—"}
              {profile.department_overridden && (
                <span className="block text-xs text-ink-muted">
                  Set by an administrator
                  {profile.department_claim ? `; Duo says “${profile.department_claim}”` : ""}.
                </span>
              )}
            </DataRow>
            <DataRow title="Organization">{profile.organization ?? "—"}</DataRow>
            <DataRow title="Duo organization claim">{profile.org_claim ?? "—"}</DataRow>
            <DataRow title="Last sign-in">{formatDate(profile.last_login_at)}</DataRow>
          </DataTable>
        </section>
      </AppShell>
    </>
  )
}
